'use client'

import { BUTTON_VARIANT } from 'fukku/Button/Button.types'
import { ButtonContentLink } from 'fukku/Button/ContentLink'
import { MODAL_TYPE, Modal, type ModalAPI } from 'fukku/Modal'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { getStoresFromGarment } from 'maps/services/client/getStoresFromGarment/getStoresFromGarment'
import { type Store } from 'maps/types'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import dynamic from 'next/dynamic'
import { useRef, useState } from 'react'

import { dataTestIds } from '../../constants/dataTestIds/dataTestIds'
import { useAnalytics } from '../../contexts/AnalyticsProvider/AnalyticsProvider'
import { useColors } from '../../contexts/ColorsProvider/ColorsProvider'
import type { CommonProps } from '../../types/CommonProps'
import { PdpActionEvents } from '../../types/PdpActionEvents'

import styles from './StoreFinder.module.scss'

const DynamicStoreFinderBody = dynamic(() =>
	import('./StoreFinderBody/StoreFinderBody').then((mod) => mod.StoreFinderBody)
)
export type StoreFinderProps = Pick<CommonProps, 'product'>

export function StoreFinder({ product }: Readonly<StoreFinderProps>) {
	const { brandId } = product
	const { t } = useLabels()
	const {
		country: { countryISO, languageISO },
	} = useMasterData()
	const { sendPdpActionEvent } = useAnalytics()
	const modalRef = useRef<ModalAPI>(null)
	const [stores, setStores] = useState<Store[]>([])
	const { selectedColor } = useColors()

	const open = async () => {
		if (!brandId) {
			return
		}

		const newStores = await getStoresFromGarment({
			productId: product.id,
			colorId: selectedColor.id,
			country: countryISO,
			language: languageISO,
		})

		sendPdpActionEvent(PdpActionEvents.AVAILABILITY_STORE)
		setStores(newStores)
		modalRef.current?.show()
	}

	return (
		<>
			<ButtonContentLink
				variant={BUTTON_VARIANT.DEFAULT}
				onClick={open}
				className={styles.button}
				data-testid={dataTestIds['pdp.productInfo.storeFinder']}
			>
				{t('pdp.storeAvailability.link')}
			</ButtonContentLink>
			<Modal
				name='store-finder-modal'
				title={t('storeLocator.storeAvailabilty.title')}
				type={MODAL_TYPE.XL}
				ref={modalRef}
				showTitle
			>
				<DynamicStoreFinderBody productId={product.id} stores={stores} />
			</Modal>
		</>
	)
}
